// SPDX-License-Identifier: AGPL-3.0-or-later
// Based on au-births by Ben Richardson — https://benrichardson.dev
// Hand-rolled SVG chart builders (no chart library). Each returns an SVG markup
// string; data marks carry [data-tip] for the global hover tooltip and
// aria-label for accessibility. Layout maths come from ./layout.ts.
import { linScale, niceTicks, binValues, gridCells, stackBands, type Bin } from './layout';
import { tipAttr } from './data';

const NS_TIP = (s: string) => `data-tip="${tipAttr(s)}"`;
const fnum = (n: number, dp = 1) => n.toLocaleString('en-AU', { minimumFractionDigits: dp, maximumFractionDigits: dp });

export interface LineSeries { label: string; colour: string; values: (number | null)[]; dash?: boolean; width?: number }
export interface Annotation { x: number; label: string }

export interface LineChartOpts {
  years: number[];
  series: LineSeries[];
  yMin?: number;
  yMax?: number;
  yLabel?: string;
  unit?: string;
  dp?: number;
  refLine?: { y: number; label: string };
  annotations?: Annotation[];
  height?: number;
  markers?: boolean;
}

const W = 900;

/** Multi-series line chart over a year axis. */
export function lineChart(o: LineChartOpts): string {
  const H = o.height ?? 380;
  const m = { t: 26, r: 20, b: 40, l: 54 };
  const iw = W - m.l - m.r;
  const ih = H - m.t - m.b;
  const dp = o.dp ?? 1;
  const all = o.series.flatMap((s) => s.values.filter((v): v is number => v != null));
  if (o.refLine) all.push(o.refLine.y);
  const yMin = o.yMin ?? Math.min(...all);
  const yMax = o.yMax ?? Math.max(...all);
  const xs = linScale(o.years[0], o.years[o.years.length - 1], m.l, m.l + iw);
  const ys = linScale(yMin, yMax, m.t + ih, m.t);

  let g = '';
  // y gridlines + labels
  for (const t of niceTicks(yMin, yMax, 5)) {
    const y = ys(t);
    g += `<line class="grid" x1="${m.l}" y1="${y.toFixed(1)}" x2="${m.l + iw}" y2="${y.toFixed(1)}"/>`;
    g += `<text class="axlabel" x="${m.l - 8}" y="${(y + 3.5).toFixed(1)}" text-anchor="end">${fnum(t, t % 1 ? dp : 0)}</text>`;
  }
  // x labels (every ~decade)
  const yrStep = Math.ceil(o.years.length / 8);
  o.years.forEach((yr, i) => {
    if (i % yrStep !== 0 && i !== o.years.length - 1) return;
    const x = xs(yr);
    g += `<text class="axlabel" x="${x.toFixed(1)}" y="${H - m.b + 18}" text-anchor="middle">${yr}</text>`;
  });
  // annotations (vertical guides)
  for (const a of o.annotations ?? []) {
    const x = xs(a.x);
    g += `<line class="annot" x1="${x.toFixed(1)}" y1="${m.t}" x2="${x.toFixed(1)}" y2="${m.t + ih}"/>`;
    g += `<text class="annot-label" x="${(x + 4).toFixed(1)}" y="${m.t + 12}" ${NS_TIP(`${a.label} (${a.x})`)}>${a.label}</text>`;
  }
  // reference line (e.g. replacement rate)
  if (o.refLine) {
    const y = ys(o.refLine.y);
    g += `<line class="refline" x1="${m.l}" y1="${y.toFixed(1)}" x2="${m.l + iw}" y2="${y.toFixed(1)}"/>`;
    g += `<text class="refline-label" x="${m.l + iw - 4}" y="${(y - 6).toFixed(1)}" text-anchor="end">${o.refLine.label}</text>`;
  }
  // series
  for (const s of o.series) {
    const pts: string[] = [];
    s.values.forEach((v, i) => { if (v != null) pts.push(`${xs(o.years[i]).toFixed(1)},${ys(v).toFixed(1)}`); });
    if (pts.length > 1) {
      g += `<polyline class="series-line" fill="none" stroke="${s.colour}" stroke-width="${s.width ?? 2.4}" ${s.dash ? 'stroke-dasharray="5 4"' : ''} points="${pts.join(' ')}"/>`;
    }
    if (o.markers !== false) {
      s.values.forEach((v, i) => {
        if (v == null) return;
        const cx = xs(o.years[i]); const cy = ys(v);
        g += `<circle class="dot" cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="6" fill="transparent" ${NS_TIP(`${s.label} · ${o.years[i]}\n${fnum(v, dp)}${o.unit ?? ''}`)}/>`;
        g += `<circle cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="2.3" fill="${s.colour}"/>`;
      });
    }
  }
  if (o.yLabel) g += `<text class="ytitle" transform="translate(14 ${m.t + ih / 2}) rotate(-90)" text-anchor="middle">${o.yLabel}</text>`;
  return `<svg viewBox="0 0 ${W} ${H}" class="chart linechart" preserveAspectRatio="xMidYMid meet" role="img">${g}</svg>`;
}

export interface BarDatum { label: string; value: number; colour?: string; tip?: string; id?: string }

/** Vertical bar chart (e.g. births per year). */
export function barChart(data: BarDatum[], o: { yLabel?: string; unit?: string; dp?: number; height?: number; colour?: string } = {}): string {
  const H = o.height ?? 340;
  const m = { t: 20, r: 16, b: 46, l: 56 };
  const iw = W - m.l - m.r;
  const ih = H - m.t - m.b;
  const dp = o.dp ?? 0;
  const max = Math.max(0, ...data.map((d) => d.value));
  const ys = linScale(0, max || 1, m.t + ih, m.t);
  const bw = (iw / Math.max(1, data.length)) * 0.72;
  const step = iw / Math.max(1, data.length);
  let g = '';
  for (const t of niceTicks(0, max, 5)) {
    const y = ys(t);
    g += `<line class="grid" x1="${m.l}" y1="${y.toFixed(1)}" x2="${m.l + iw}" y2="${y.toFixed(1)}"/>`;
    g += `<text class="axlabel" x="${m.l - 8}" y="${(y + 3.5).toFixed(1)}" text-anchor="end">${fnum(t, 0)}</text>`;
  }
  const labStep = Math.ceil(data.length / 12);
  data.forEach((d, i) => {
    const x = m.l + i * step + (step - bw) / 2;
    const y = ys(d.value);
    const h = Math.max(0, m.t + ih - y);
    g += `<rect class="bar" x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${bw.toFixed(1)}" height="${h.toFixed(1)}" rx="2" fill="${d.colour ?? o.colour ?? '#0d9488'}" ${d.id ? `data-id="${d.id}"` : ''} ${NS_TIP(d.tip ?? `${d.label}\n${fnum(d.value, dp)}${o.unit ?? ''}`)}/>`;
    if (i % labStep === 0 || i === data.length - 1) g += `<text class="axlabel" x="${(x + bw / 2).toFixed(1)}" y="${H - m.b + 18}" text-anchor="middle">${d.label}</text>`;
  });
  if (o.yLabel) g += `<text class="ytitle" transform="translate(14 ${m.t + ih / 2}) rotate(-90)" text-anchor="middle">${o.yLabel}</text>`;
  return `<svg viewBox="0 0 ${W} ${H}" class="chart barchart" preserveAspectRatio="xMidYMid meet" role="img">${g}</svg>`;
}

/** Horizontal ranked bars (states / categories). Supports a reference marker. */
export function hBars(data: BarDatum[], o: { unit?: string; dp?: number; ref?: { value: number; label: string }; diverge?: boolean; rowH?: number } = {}): string {
  const rowH = o.rowH ?? 30;
  const m = { t: 8, r: 20, b: 8, l: 116 };
  const H = m.t + m.b + data.length * rowH;
  const iw = W - m.l - m.r;
  const dp = o.dp ?? 1;
  const vals = data.map((d) => d.value);
  const lo = Math.min(0, ...vals, o.ref ? o.ref.value : 0);
  const hi = Math.max(0, ...vals, o.ref ? o.ref.value : 0);
  const xs = linScale(lo, hi || 1, m.l, m.l + iw);
  const zero = xs(0);
  let g = '';
  if (o.ref) {
    const rx = xs(o.ref.value);
    g += `<line class="refline" x1="${rx.toFixed(1)}" y1="${m.t}" x2="${rx.toFixed(1)}" y2="${(H - m.b).toFixed(1)}"/>`;
    g += `<text class="refline-label" x="${(rx).toFixed(1)}" y="${(m.t + 10).toFixed(1)}" text-anchor="middle">${o.ref.label}</text>`;
  }
  data.forEach((d, i) => {
    const y = m.t + i * rowH + 4;
    const bh = rowH - 10;
    const x = Math.min(zero, xs(d.value));
    const w = Math.abs(xs(d.value) - zero);
    g += `<text class="bar-lab" x="${m.l - 8}" y="${(y + bh / 2 + 3.5).toFixed(1)}" text-anchor="end">${d.label}</text>`;
    g += `<rect class="hbar" x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${Math.max(1, w).toFixed(1)}" height="${bh}" rx="2.5" fill="${d.colour ?? '#0d9488'}" ${d.id ? `data-id="${d.id}"` : ''} ${NS_TIP(d.tip ?? `${d.label}\n${fnum(d.value, dp)}${o.unit ?? ''}`)}/>`;
    g += `<text class="bar-val" x="${(xs(d.value) + (d.value >= 0 ? 6 : -6)).toFixed(1)}" y="${(y + bh / 2 + 3.5).toFixed(1)}" text-anchor="${d.value >= 0 ? 'start' : 'end'}">${fnum(d.value, dp)}${o.unit ?? ''}</text>`;
  });
  return `<svg viewBox="0 0 ${W} ${H}" class="chart hbars" preserveAspectRatio="xMidYMid meet" role="img">${g}</svg>`;
}

/** Stacked area chart (e.g. nuptial vs ex-nuptial births), values as absolute or %. */
export function stackedArea(years: number[], series: LineSeries[], o: { unit?: string; dp?: number; percent?: boolean; height?: number; yLabel?: string } = {}): string {
  const H = o.height ?? 360;
  const m = { t: 20, r: 18, b: 40, l: 58 };
  const iw = W - m.l - m.r;
  const ih = H - m.t - m.b;
  const dp = o.dp ?? 0;
  const raw = series.map((s) => s.values.map((v) => (v != null ? v : 0)));
  let stackVals = raw;
  if (o.percent) {
    stackVals = raw.map((s, si) => s.map((_, i) => {
      const tot = raw.reduce((a, r) => a + r[i], 0);
      return tot > 0 ? (raw[si][i] / tot) * 100 : 0;
    }));
  }
  const bands = stackBands(stackVals, years.length);
  const totalMax = o.percent ? 100 : Math.max(1, ...years.map((_, i) => stackVals.reduce((a, s) => a + s[i], 0)));
  const xs = linScale(years[0], years[years.length - 1], m.l, m.l + iw);
  const ys = linScale(0, totalMax, m.t + ih, m.t);
  let g = '';
  for (const t of niceTicks(0, totalMax, 5)) {
    const y = ys(t);
    g += `<line class="grid" x1="${m.l}" y1="${y.toFixed(1)}" x2="${m.l + iw}" y2="${y.toFixed(1)}"/>`;
    g += `<text class="axlabel" x="${m.l - 8}" y="${(y + 3.5).toFixed(1)}" text-anchor="end">${fnum(t, 0)}${o.percent ? '%' : ''}</text>`;
  }
  const yrStep = Math.ceil(years.length / 8);
  years.forEach((yr, i) => { if (i % yrStep === 0 || i === years.length - 1) g += `<text class="axlabel" x="${xs(yr).toFixed(1)}" y="${H - m.b + 18}" text-anchor="middle">${yr}</text>`; });
  series.forEach((s, si) => {
    const top = bands[si].map((b, i) => `${xs(years[i]).toFixed(1)},${ys(b.hi).toFixed(1)}`);
    const bot = bands[si].map((b, i) => `${xs(years[i]).toFixed(1)},${ys(b.lo).toFixed(1)}`).reverse();
    g += `<polygon class="area" points="${[...top, ...bot].join(' ')}" fill="${s.colour}" fill-opacity="0.82" ${NS_TIP(`${s.label}`)}/>`;
    // hover hit points along the top edge
    bands[si].forEach((b, i) => {
      const v = stackVals[si][i];
      g += `<circle cx="${xs(years[i]).toFixed(1)}" cy="${ys(b.hi).toFixed(1)}" r="6" fill="transparent" ${NS_TIP(`${s.label} · ${years[i]}\n${fnum(v, dp)}${o.percent ? '%' : (o.unit ?? '')}`)}/>`;
    });
  });
  if (o.yLabel) g += `<text class="ytitle" transform="translate(14 ${m.t + ih / 2}) rotate(-90)" text-anchor="middle">${o.yLabel}</text>`;
  return `<svg viewBox="0 0 ${W} ${H}" class="chart stacked" preserveAspectRatio="xMidYMid meet" role="img">${g}</svg>`;
}

export interface HeatCell { row: number; col: number; value: number | null; tip: string }
export interface HeatmapOpts {
  rows: number; cols: number; rowLabels: string[]; colLabels: string[];
  cells: HeatCell[]; ramp: (t: number) => string; vMin?: number; vMax?: number;
  cellH?: number; colLabelStep?: number; title?: string;
}

/** Grid heatmap using gridCells; every cell carries a data-tip. */
export function heatmap(o: HeatmapOpts): string {
  const cellH = o.cellH ?? 26;
  const m = { t: 24, r: 12, b: 8, l: 62 };
  const iw = W - m.l - m.r;
  const ih = o.rows * cellH;
  const H = m.t + ih + m.b;
  const cells = gridCells(o.cols, o.rows, iw, ih, 1.5);
  const finite = o.cells.map((c) => c.value).filter((v): v is number => v != null);
  const vMin = o.vMin ?? (finite.length ? Math.min(...finite) : 0);
  const vMax = o.vMax ?? (finite.length ? Math.max(...finite) : 1);
  const norm = (v: number) => (vMax === vMin ? 0.5 : (v - vMin) / (vMax - vMin));
  const at = new Map(o.cells.map((c) => [`${c.row}:${c.col}`, c]));
  let g = '';
  // row labels
  o.rowLabels.forEach((lab, r) => {
    const cy = m.t + r * (ih / o.rows) + (ih / o.rows) / 2 + 3.5;
    g += `<text class="heat-rowlab" x="${m.l - 8}" y="${cy.toFixed(1)}" text-anchor="end">${lab}</text>`;
  });
  // col labels
  const step = o.colLabelStep ?? Math.ceil(o.cols / 10);
  o.colLabels.forEach((lab, c) => {
    if (c % step !== 0 && c !== o.cols - 1) return;
    const cx = m.l + (c + 0.5) * (iw / o.cols);
    g += `<text class="heat-collab" x="${cx.toFixed(1)}" y="${m.t - 8}" text-anchor="middle">${lab}</text>`;
  });
  for (const cell of cells) {
    const d = at.get(`${cell.row}:${cell.col}`);
    const x = m.l + cell.x; const y = m.t + cell.y;
    const fill = d && d.value != null ? o.ramp(norm(d.value)) : '#eef1f4';
    g += `<rect class="heat-cell" x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${cell.w.toFixed(1)}" height="${cell.h.toFixed(1)}" fill="${fill}" ${d ? NS_TIP(d.tip) : ''}/>`;
  }
  return `<svg viewBox="0 0 ${W} ${H}" class="chart heatmap" preserveAspectRatio="xMidYMid meet" role="img">${g}</svg>`;
}

/** Histogram over `values`, using binValues. Bars carry data-id = bin index for filtering. */
export function histogram(values: number[], o: { bins?: number; unit?: string; dp?: number; height?: number; colour?: string; domainMin?: number; domainMax?: number; activeBin?: number | null } = {}): { svg: string; binsInfo: Bin[] } {
  const H = o.height ?? 220;
  const m = { t: 14, r: 12, b: 34, l: 40 };
  const iw = W - m.l - m.r;
  const ih = H - m.t - m.b;
  const nb = o.bins ?? 12;
  const { bins } = binValues(values, nb, o.domainMin, o.domainMax);
  const maxCount = Math.max(1, ...bins.map((b) => b.count));
  const dp = o.dp ?? 1;
  const step = iw / bins.length;
  const ys = linScale(0, maxCount, m.t + ih, m.t);
  let g = '';
  for (const t of niceTicks(0, maxCount, 4)) {
    const y = ys(t);
    g += `<line class="grid" x1="${m.l}" y1="${y.toFixed(1)}" x2="${m.l + iw}" y2="${y.toFixed(1)}"/>`;
    g += `<text class="axlabel" x="${m.l - 6}" y="${(y + 3.5).toFixed(1)}" text-anchor="end">${Math.round(t)}</text>`;
  }
  bins.forEach((b, i) => {
    const x = m.l + i * step + 1.5;
    const bw = step - 3;
    const y = ys(b.count);
    const h = Math.max(0, m.t + ih - y);
    const active = o.activeBin === i;
    g += `<rect class="histbar${active ? ' active' : ''}" data-bin="${i}" x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${bw.toFixed(1)}" height="${h.toFixed(1)}" rx="2" fill="${active ? '#0f766e' : (o.colour ?? '#5eead4')}" ${NS_TIP(`${fnum(b.x0, dp)}–${fnum(b.x1, dp)}${o.unit ?? ''}\n${b.count} ${b.count === 1 ? 'entry' : 'entries'}`)}/>`;
    if (i % 2 === 0 || i === bins.length - 1) g += `<text class="axlabel" x="${(x + bw / 2).toFixed(1)}" y="${H - m.b + 16}" text-anchor="middle">${fnum(b.x0, dp)}</text>`;
  });
  return { svg: `<svg viewBox="0 0 ${W} ${H}" class="chart histogram" preserveAspectRatio="none" role="img">${g}</svg>`, binsInfo: bins };
}

/** Tiny inline sparkline for table cells. */
export function sparkline(values: (number | null)[], colour = '#0d9488', w = 108, h = 26): string {
  const finite = values.filter((v): v is number => v != null);
  if (finite.length < 2) return `<svg width="${w}" height="${h}" class="spark"></svg>`;
  const lo = Math.min(...finite); const hi = Math.max(...finite);
  const xs = linScale(0, values.length - 1, 2, w - 2);
  const ys = linScale(lo, hi || 1, h - 3, 3);
  const pts: string[] = [];
  values.forEach((v, i) => { if (v != null) pts.push(`${xs(i).toFixed(1)},${ys(v).toFixed(1)}`); });
  const last = values.reduce<{ i: number; v: number } | null>((acc, v, i) => (v != null ? { i, v } : acc), null);
  return `<svg width="${w}" height="${h}" class="spark" viewBox="0 0 ${w} ${h}" preserveAspectRatio="none">` +
    `<polyline fill="none" stroke="${colour}" stroke-width="1.6" points="${pts.join(' ')}"/>` +
    (last ? `<circle cx="${xs(last.i).toFixed(1)}" cy="${ys(last.v).toFixed(1)}" r="1.8" fill="${colour}"/>` : '') +
    `</svg>`;
}

// ── Colour ramps ───────────────────────────────────────────────────────────
function lerp(a: number, b: number, t: number): number { return a + (b - a) * t; }
function mix(c1: number[], c2: number[], t: number): string {
  return `rgb(${Math.round(lerp(c1[0], c2[0], t))},${Math.round(lerp(c1[1], c2[1], t))},${Math.round(lerp(c1[2], c2[2], t))})`;
}
/** Sequential teal ramp (light → deep). */
export function tealRamp(t: number): string {
  const tc = Math.max(0, Math.min(1, t));
  return tc < 0.5 ? mix([224, 247, 244], [45, 212, 191], tc / 0.5) : mix([45, 212, 191], [15, 76, 70], (tc - 0.5) / 0.5);
}
/** Warm ramp (light → deep amber/coral) for the fertility surface. */
export function warmRamp(t: number): string {
  const tc = Math.max(0, Math.min(1, t));
  return tc < 0.5 ? mix([255, 247, 237], [251, 191, 36], tc / 0.5) : mix([251, 191, 36], [190, 24, 93], (tc - 0.5) / 0.5);
}
/** Diverging ramp centred on 0.5: red (low) → neutral → green (high). */
export function divergeRamp(t: number): string {
  const tc = Math.max(0, Math.min(1, t));
  return tc < 0.5 ? mix([220, 38, 38], [241, 245, 249], tc / 0.5) : mix([241, 245, 249], [22, 163, 74], (tc - 0.5) / 0.5);
}
